import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../services/authService";
import { Footer } from "../components/layout/Footer";

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);

  const userId = searchParams.get("user_id") || searchParams.get("userId");
  const code = searchParams.get("code") || searchParams.get("token");

  useEffect(() => {
    if (!userId || !code) {
      setInvalidLink(true);
    }
  }, [userId, code]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Check password strength
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!hasUppercase || !hasLowercase || !hasDigit || !hasSpecial) {
      setError(
        "Password must contain at least one uppercase letter, one lowercase letter, one digit, and one special character",
      );
      return;
    }

    if (!userId || !code) {
      setError("Invalid reset link");
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(userId, code, password);
      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to reset password. The link may have expired.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Invalid link page
  if (invalidLink) {
    return (
      <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex animated-gradient">
        {/* Left side - Branding/Hero */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover scale-105"
          >
            <source src="/header.mp4?v=2" type="video/mp4" />
          </video>

          {/* Overlay for readability */}
          <div className="absolute inset-0 bg-surface-950/50" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center p-12">
            {/* Colvix logo */}
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
            >
              <img
                src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8"
              />
            </a>

            {/* TradeCraft branding */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src="/logo.svg"
                  alt="TradeCraft"
                  className="w-16 h-16 drop-shadow-lg"
                />
                <div>
                  <h1 className="text-4xl font-bold gradient-text">
                    TradeCraft
                  </h1>
                  <p className="text-surface-400 text-lg">Algo Builder</p>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-red-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
        </div>

        {/* Right side - Invalid link message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile branding */}
            <div className="lg:hidden text-center mb-8">
              <a
                href="https://www.colvix.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/colvix_header_final.png"
                  alt="Colvix"
                  className="colvix-logo h-6 mx-auto"
                />
              </a>
              <div className="flex items-center justify-center gap-3 mb-2">
                <img
                  src="/logo.svg"
                  alt="TradeCraft"
                  className="w-12 h-12"
                />
                <h1 className="text-2xl font-bold gradient-text">
                  TradeCraft
                </h1>
              </div>
              <p className="text-surface-400">Algo Builder</p>
            </div>

            {/* Invalid link card */}
            <div className="glass-card p-8 animate-slide-up">
              <div className="text-center">
                <div className="mx-auto h-16 w-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-surface-100 mb-2">
                  Invalid Reset Link
                </h2>
                <p className="text-surface-400 mb-6">
                  This password reset link is invalid or has expired.
                </p>
              </div>

              <div className="space-y-3">
                <Link
                  to="/forgot-password"
                  className="btn-primary w-full inline-block text-center"
                >
                  Request New Reset Link
                </Link>
                <Link
                  to="/login"
                  className="btn-secondary w-full inline-block text-center"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
    );
  }

  // Success page
  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex animated-gradient">
        {/* Left side - Branding/Hero */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover scale-105"
          >
            <source src="/header.mp4?v=2" type="video/mp4" />
          </video>

          {/* Overlay for readability */}
          <div className="absolute inset-0 bg-surface-950/50" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center p-12">
            {/* Colvix logo */}
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
            >
              <img
                src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8"
              />
            </a>

            {/* TradeCraft branding */}
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-6">
                <img
                  src="/logo.svg"
                  alt="TradeCraft"
                  className="w-16 h-16 drop-shadow-lg"
                />
                <div>
                  <h1 className="text-4xl font-bold gradient-text">
                    TradeCraft
                  </h1>
                  <p className="text-surface-400 text-lg">Algo Builder</p>
                </div>
              </div>
            </div>

            {/* Success features */}
            <div className="space-y-4">
              {[
                {
                  icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                  title: "Password Updated",
                  desc: "Your password has been reset",
                },
                {
                  icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
                  title: "Secure Access",
                  desc: "Log in with your new password",
                },
                {
                  icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                  title: "Start Trading",
                  desc: "Get back to your strategies",
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={feature.icon}
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-surface-100">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-surface-400">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-20 right-20 w-64 h-64 bg-green-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
        </div>

        {/* Right side - Success message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile branding */}
            <div className="lg:hidden text-center mb-8">
              <a
                href="https://www.colvix.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/colvix_header_final.png"
                  alt="Colvix"
                  className="colvix-logo h-6 mx-auto"
                />
              </a>
              <div className="flex items-center justify-center gap-3 mb-2">
                <img
                  src="/logo.svg"
                  alt="TradeCraft"
                  className="w-12 h-12"
                />
                <h1 className="text-2xl font-bold gradient-text">
                  TradeCraft
                </h1>
              </div>
              <p className="text-surface-400">Algo Builder</p>
            </div>

            {/* Success card */}
            <div className="glass-card p-8 animate-slide-up">
              <div className="text-center">
                <div className="mx-auto h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                  <svg
                    className="w-8 h-8 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-surface-100 mb-2">
                  Password Reset!
                </h2>
                <p className="text-surface-400 mb-2">
                  Your password has been successfully reset.
                </p>
                <p className="text-surface-500 text-sm mb-6">
                  You can now log in with your new password.
                </p>
              </div>

              <button
                onClick={() => navigate("/login")}
                className="btn-primary w-full"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex animated-gradient">
      {/* Left side - Branding/Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-105"
        >
          <source src="/header.mp4?v=2" type="video/mp4" />
        </video>

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-surface-950/50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/70 via-surface-950/40 to-accent-900/30" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12">
          {/* Colvix logo */}
          <a
            href="https://www.colvix.net"
            target="_blank"
            rel="noopener noreferrer"
            className="mb-8 opacity-80 hover:opacity-100 transition-opacity inline-block"
          >
            <img src="/colvix_header_final.png" alt="Colvix" className="colvix-logo h-8" />
          </a>

          {/* TradeCraft branding */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-16 h-16 drop-shadow-lg"
              />
              <div>
                <h1 className="text-4xl font-bold gradient-text">
                  TradeCraft
                </h1>
                <p className="text-surface-400 text-lg">Algo Builder</p>
              </div>
            </div>
          </div>

          {/* Features list */}
          <div className="space-y-4">
            {[
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Comprehensive Backtesting",
                desc: "Test strategies with historical data",
              },
              {
                icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                title: "Performance Analytics",
                desc: "Sharpe ratio, drawdown, and more",
              },
              {
                icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Portfolio Management",
                desc: "Track your investment strategies",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-4 rounded-xl bg-surface-900/30 backdrop-blur-sm border border-surface-700/30"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-5 h-5 text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={feature.icon}
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-surface-100">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-surface-400">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      {/* Right side - Reset password form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <a
              href="https://www.colvix.net"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mb-4 opacity-80 hover:opacity-100 transition-opacity"
            >
              <img
                src="/colvix_header_final.png"
                alt="Colvix"
                className="colvix-logo h-6 mx-auto"
              />
            </a>
            <div className="flex items-center justify-center gap-3 mb-2">
              <img src="/logo.svg" alt="TradeCraft" className="w-12 h-12" />
              <h1 className="text-2xl font-bold gradient-text">
                TradeCraft
              </h1>
            </div>
            <p className="text-surface-400">Algo Builder</p>
          </div>

          {/* Reset password card */}
          <div className="glass-card p-8 animate-slide-up">
            <div className="text-center mb-8">
              <div className="mx-auto h-14 w-14 bg-primary-500/20 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-7 h-7 text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-surface-100">
                Reset Password
              </h2>
              <p className="text-surface-400 mt-2">
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-red-400 text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field w-full"
                  placeholder="••••••••"
                  required
                  autoFocus
                />
                <p className="mt-1 text-xs text-surface-500">
                  Min 8 chars with uppercase, lowercase, number & special
                  character
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-field w-full"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Resetting...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-surface-400 hover:text-primary-400 transition-colors"
              >
                <svg
                  className="h-4 w-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
